import React, { useState } from "react";
import { Sparkles, Mail, Check, AlertCircle, Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "./ui/dialog";
import { postJSON } from "../lib/api";

/**
 * WaitlistButton — replaces a Stripe "Upgrade" CTA while STRIPE_WAITLIST_MODE
 * is true. Opens a modal with an email capture form that posts to
 * /api/billing/waitlist.
 *
 * Props:
 *  - source        : free-form tag persisted with the signup row
 *                    (e.g. "pricing:pro_monthly", "homepage_hero",
 *                    "wizard_paywall"). Useful for analytics.
 *  - variant       : "primary" → cyan filled; "secondary" → outlined.
 *                    Defaults to "primary".
 *  - children      : optional label. Defaults to
 *                    "Get notified when Pro launches".
 *  - className     : extra Tailwind classes for the trigger button.
 *  - dataTestId    : test id prefix; the input/submit/success states append
 *                    "-input", "-submit", "-success" automatically.
 */
export const WaitlistButton = ({
    source = "unknown",
    variant = "primary",
    children = "Get notified when Pro launches",
    className = "",
    dataTestId = "waitlist-button",
}) => {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle"); // idle | submitting | success | already | error
    const [errorMsg, setErrorMsg] = useState("");
    const [savedEmail, setSavedEmail] = useState("");

    const reset = () => {
        setEmail("");
        setStatus("idle");
        setErrorMsg("");
        setSavedEmail("");
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const trimmed = (email || "").trim();
        if (!trimmed) {
            setErrorMsg("Please enter a valid email address.");
            setStatus("error");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setErrorMsg("That email doesn’t look right.");
            setStatus("error");
            return;
        }
        setStatus("submitting");
        setErrorMsg("");
        try {
            const referrer =
                typeof window !== "undefined" ? window.location.href : null;
            const r = await postJSON("/billing/waitlist", {
                email: trimmed,
                source,
                referrer_url: referrer,
            });
            setSavedEmail(trimmed);
            setStatus(r?.already_subscribed ? "already" : "success");
        } catch (err) {
            const payload = err?.payload;
            const msg =
                payload?.detail?.message ||
                payload?.detail ||
                err?.message ||
                "Could not save your signup. Please try again in a moment.";
            setErrorMsg(typeof msg === "string" ? msg : "Something went wrong.");
            setStatus("error");
        }
    };

    const triggerClass =
        variant === "secondary"
            ? "w-full py-2.5 rounded-md bg-elevated/60 text-text-primary border border-white/[0.08] text-[13px] font-medium hover:border-accent-cyan/40 hover:text-accent-cyan transition-colors duration-200"
            : "w-full py-2.5 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan transition-all duration-200 inline-flex items-center justify-center gap-1.5";

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) {
                    // Defer reset so the closing animation doesn't flash an empty state
                    setTimeout(reset, 200);
                }
            }}
        >
            <DialogTrigger asChild>
                <button
                    type="button"
                    className={`${triggerClass} ${className}`.trim()}
                    data-testid={dataTestId}
                    aria-label="Join the Pro launch waitlist"
                >
                    {variant === "primary" && (
                        <Sparkles className="w-3.5 h-3.5" />
                    )}
                    {children}
                </button>
            </DialogTrigger>
            <DialogContent
                className="bg-surface border-white/[0.08] text-text-primary sm:max-w-md"
                data-testid={`${dataTestId}-modal`}
            >
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-text-primary">
                        Be the first to know when Pro launches
                    </DialogTitle>
                    <DialogDescription className="text-[13px] text-text-muted leading-relaxed pt-1">
                        Pro tier ($19.90/mo) is launching soon. Enter your email and
                        we&apos;ll notify you the moment it goes live. No spam, no
                        third-party sharing.
                    </DialogDescription>
                </DialogHeader>

                {(status === "success" || status === "already") && (
                    <div
                        className="flex items-start gap-3 rounded-lg border border-accent-mint/40 bg-accent-mint/[0.06] p-4"
                        data-testid={`${dataTestId}-success`}
                    >
                        <Check className="w-4 h-4 text-accent-mint mt-0.5 shrink-0" />
                        <p className="text-[13.5px] text-text-primary leading-relaxed">
                            {status === "already" ? (
                                <>
                                    You&apos;re already on the waitlist! We&apos;ll be in
                                    touch.
                                </>
                            ) : (
                                <>
                                    You&apos;re on the list. We&apos;ll email you at{" "}
                                    <span className="font-medium text-accent-mint break-all">
                                        {savedEmail}
                                    </span>{" "}
                                    when Pro is live.
                                </>
                            )}
                        </p>
                    </div>
                )}

                {status !== "success" && status !== "already" && (
                    <form
                        onSubmit={onSubmit}
                        className="space-y-3"
                        data-testid={`${dataTestId}-form`}
                    >
                        <label className="block">
                            <span className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim">
                                Email
                            </span>
                            <div className="mt-1.5 relative">
                                <Mail className="w-3.5 h-3.5 text-text-dim absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    autoComplete="email"
                                    required
                                    maxLength={254}
                                    disabled={status === "submitting"}
                                    className="w-full pl-9 pr-3 py-2.5 rounded-md bg-canvas border border-white/[0.08] text-[14px] text-text-primary placeholder:text-text-dim focus:outline-none focus:border-accent-cyan/40 disabled:opacity-50"
                                    data-testid={`${dataTestId}-input`}
                                />
                            </div>
                        </label>

                        {status === "error" && errorMsg && (
                            <div
                                className="flex items-start gap-2 text-[12.5px] text-accent-red"
                                data-testid={`${dataTestId}-error`}
                            >
                                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                <span>{errorMsg}</span>
                            </div>
                        )}

                        <DialogFooter className="!justify-start pt-2">
                            <button
                                type="submit"
                                disabled={status === "submitting"}
                                className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-cyan text-canvas font-medium text-[13.5px] hover:bg-accent-cyan/90 transition-colors disabled:opacity-50"
                                data-testid={`${dataTestId}-submit`}
                            >
                                {status === "submitting" ? (
                                    <>
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        Saving…
                                    </>
                                ) : (
                                    <>Notify me</>
                                )}
                            </button>
                        </DialogFooter>

                        <p className="text-[11px] text-text-dim leading-relaxed pt-1">
                            By joining, you agree to receive a one-time launch email
                            from Cybersecurity Forefront LLC. See our{" "}
                            <a
                                href="/privacy"
                                className="text-accent-cyan hover:underline"
                            >
                                Privacy Policy
                            </a>
                            .
                        </p>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default WaitlistButton;
